<template>
  <BaseLayout title="Evaluations">
    <!-- Main Content (Center)-->
    <div class="container my-4">
      <h1 v-if="isTeacher && evaluationStats" class="display-4 text-center">Evaluations overview</h1>
      <h1 v-else-if="isTeacher" class="display-4 text-center">Student evaluations</h1>
      <h1 v-else class="display-4 text-center">Evaluations</h1>
      <!-- Loading -->
      <div v-if="loading" class="text-center my-4">
        <div class="spinner-border" role="status">
          <span class="sr-only">&nbsp;&nbsp;Loading...</span>
        </div>
      </div>
      <div v-else>
        <div v-if="isTeacher && evaluationStats && subtaskSummary" class="card mb-4">
          <div class="card-body">
            <!-- Evaluation Stats Section -->
            <div v-if="evaluationStats" class="mb-4">
              <div class="row mb-4">
                <div class="col-md-4 mb-2">
                  <div class="card">
                  <div class="card-body">
                    <p class="card-text"><strong>Homework:</strong> {{ evaluationStats.homework_title }}</p>
                  </div>
                  </div>
                </div>
                <div class="col-md-4 mb-2">
                  <div class="card">
                  <div class="card-body">
                    <p class="card-text"><strong>Deadline:</strong> {{ formatDate(evaluationStats.deadline_date) }}</p>
                  </div>
                  </div>
                </div>
                <div class="col-md-4 mb-2">
                  <div class="card">
                  <div class="card-body">
                    <p class="card-text"><strong>Deadline Type:</strong> {{ getEvaluationTypeLabel(evaluationStats.deadline_evaluation_type) }}</p>
                  </div>
                  </div>
                </div>
              </div>
              <div class="alert alert-info mt-3" role="alert"
                v-if="evaluationStats.run_canceled">
                <p class="card-text">Some evaluations from this run were canceled. Overview count only finished evaluations.</p>
              </div>
              <h5>Evaluation stats</h5>
                <ul class="list-group">
                <li class="list-group-item d-flex align-items-center">
                  <i class="bi bi-people-fill text-primary me-2"></i>
                  Total students:  {{ evaluationStats.total_students }}
                </li>
                <li class="list-group-item d-flex align-items-center">
                  <i class="bi bi-check-square text-success me-2"></i>
                  Evaluations ended:  {{ evaluationStats.finished_evaluations }}
                </li>
                <li class="list-group-item d-flex align-items-center">
                  <i class="bi bi-hourglass-split text-warning me-2"></i>
                  Evaluations not finished: {{ evaluationStats.not_finished_evaluations }}
                </li>
                </ul>
            </div>
            <!-- Subtask Summary Section -->
            <div v-if="subtaskSummary">
              <h5>Subtask Summary</h5>
              <div class="row">
                <div v-for="(data, subtaskId) in groupedSubtasks" :key="subtaskId" class="col-md-6 mb-3">
                  <div class="card">
                    <div class="card-header">
                      {{ data.subtask_title }}
                    </div>
                    <div v-if="!data.to_evaluate" class="card-body">
                      <p class="card-text">
                        <i class="bi bi-info-circle-fill me-2"></i>
                        Task not to be evaluated.
                      </p>
                    </div>
                    <div v-else>
                      <ul class="list-group list-group-flush">
                        <li class="list-group-item d-flex justify-content-between align-items-center">
                          <span>
                            <i class="bi bi-check-circle-fill text-success me-2"></i>Success: {{ data.success }}
                          </span>
                            <button v-if="data.success > 0" class="btn btn-primary btn-sm" @click="openSpecificStudentList('success', subtaskId)">
                            <i class="bi bi-search me-1"></i> View students
                            </button>
                        </li>
                        <li class="list-group-item d-flex justify-content-between align-items-center">
                          <span>
                            <i class="bi bi-x-circle-fill text-danger me-2"></i>Fail: {{ data.fail }}
                          </span>
                          <button v-if="data.fail > 0" class="btn btn-primary btn-sm" @click="openSpecificStudentList('fail', subtaskId)">
                            <i class="bi bi-search me-1"></i> View students
                          </button>
                        </li>
                        <li class="list-group-item d-flex justify-content-between align-items-center">
                          <span>
                            <i class="bi bi-file-earmark-x text-info me-2"></i>No Submission: {{ data.missing }}
                          </span>
                          <button v-if="data.missing > 0" class="btn btn-primary btn-sm" @click="openSpecificStudentList('missing', subtaskId)">
                            <i class="bi bi-search me-1"></i> View students
                          </button>
                        </li>
                        <li class="list-group-item d-flex justify-content-between align-items-center">
                          <span>
                            <i class="bi bi-exclamation-triangle-fill text-warning me-2"></i>Internal Error: {{ data.container_error }}
                          </span>
                          <button v-if="data.container_error > 0" class="btn btn-primary btn-sm" @click="openSpecificStudentList('container_error', subtaskId)">
                            <i class="bi bi-search me-1"></i> View students
                          </button>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div v-else-if="evaluations.length === 0" class="alert alert-info text-center">
          No evaluations available.
          <br>
          <button @click="fetchAgainEvaluations()" class="btn btn-primary">
            <i class="bi bi-arrow-clockwise me-1"></i> Refresh
          </button>
        </div>
        <div v-else>
          <div v-for="(group, homeworkId) in evaluationsByHomework" :key="homeworkId" class="mb-4">
            <h2 class="mb-3">{{ group.homework.title }}</h2>
            <!-- Display each evaluation as a line in a list -->
            <ul class="list-group">
              <li v-for="evaluation in group.evaluations" :key="evaluation.id" class="list-group-item d-flex justify-content-between align-items-center">
                <div>
                  <span v-if="evaluation.container_error == true" class="badge text-bg-danger me-3">Internal error</span>
                  <span class="me-3"><strong>Deadline:</strong> {{ formatDate(evaluation.deadline.date) }}</span>
                  <span class="me-3">
                    <strong>Result:  </strong>
                    <i :class="evaluation.subevaluations_passed === true ?
                            'bi bi-check-circle-fill text-success' :
                            (evaluation.subevaluations_passed === false ?
                            'bi bi-x-circle-fill text-danger' :
                            'bi bi-hourglass-split text-warning')">
                    </i>
                  </span>
                  <span class="me-3"><strong>Type:</strong> {{ getEvaluationTypeLabel(evaluation.deadline.evaluation_type) }}</span>
                </div>
                <router-link :to="`evaluation/${evaluation.id}`" class="btn btn-primary btn-sm">
                  <i class="bi bi-eye"></i> View
                </router-link>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>

    <!-- Right Sidebar Slot -->
    <template v-slot:right-sidebar>
      <div v-if="isTeacher" class="card p-3">
        <h5 class="card-title">Quick Actions:</h5>
        <button class="btn btn-primary mb-2" data-bs-toggle="modal" data-bs-target="#chooseStudentByUcoModalForm">
          <i class="bi bi-search me-1"></i> Search for Student
        </button>
        <button class="btn btn-primary mb-2" data-bs-toggle="modal" data-bs-target="#chooseHomeworkDeadlineModalForm">
          <i class="bi bi-calendar-plus me-1"></i> Search for Evaluation
        </button>
        <button class="btn btn-success mb-2" data-bs-toggle="modal" data-bs-target="#evaluationStartModalForm">
          <i class="bi bi-play-circle me-1"></i> Start evaluation
        </button>
        <button class="btn btn-danger" data-bs-toggle="modal" data-bs-target="#chooseHomeworkDeadlineToDeleteRunModalForm">
          <i class="bi bi-trash me-1"></i> Delete evaluation run
        </button>
      </div>
    </template>

    <!-- Modal for select student -->
    <ChooseStudentByUcoModalForm v-if="isTeacher"
      title="Search for student"
      ref="ChooseStudentByUcoModalForm"
      @submitForm="searchStudent"
    />

    <!-- Modal for start evaluation -->
    <EvaluationStartModalForm v-if="isTeacher"
      ref="EvaluationStartModalForm"
    />

    <!-- Modal for choose homework deadline -->
    <ChooseHomeworkDeadlineModalForm v-if="isTeacher"
      ref="ChooseHomeworkDeadlineModalForm"
      @submitForm="fetchEvaluationStatsAndSubtaskSummary"
    />

    <!-- Modal for choose homework deadline -->
    <ChooseHomeworkDeadlineToDeleteRunModalForm v-if="isTeacher"
      ref="ChooseHomeworkDeadlineToDeleteRunModalForm"
      @submitForm="deleteEvaluationRun"
    />

    <!-- Modal for specific student list -->
    <SpecificStudentList 
      v-if="isTeacher" 
      :specifiedData="specifiedData" 
      ref="SpecificStudentListModal" 
      :onRedirectEvaluation="handleRedirectEvaluation" 
    />

    <StemErrorModal ref="stemErrorModal" />
  </BaseLayout>
</template>

<script>
import { Modal } from 'bootstrap';
import BaseLayout from '@/components/BaseLayout.vue';
import EvaluationController from '@/controllers/EvaluationController';
import StudentController from '@/controllers/StudentController';
import EvaluationStartModalForm from '@/components/EvaluationStartModalForm.vue';
import ChooseStudentByUcoModalForm from '@/components/ChooseStudentByUcoModalForm.vue';
import ChooseHomeworkDeadlineToDeleteRunModalForm from '@/components/ChooseHomeworkDeadlineToDeleteRunModalForm.vue';
import StemErrorModal from '@/components/StemErrorModal.vue';
import DeadlineController from '@/controllers/DeadlineController';
import ChooseHomeworkDeadlineModalForm from '@/components/ChooseHomeworkDeadlineModalForm.vue';
import Deadline from '@/models/deadline';
import SpecificStudentList from '@/components/SpecificStudentListModal.vue';
import { mapGetters } from 'vuex';

export default {
  name: 'EvaluationView',
  components: {
    BaseLayout,
    EvaluationStartModalForm,
    ChooseStudentByUcoModalForm,
    ChooseHomeworkDeadlineModalForm,
    ChooseHomeworkDeadlineToDeleteRunModalForm,
    SpecificStudentList,
    StemErrorModal
  },
  data() {
    return {
      evaluations: [],
      evaluationStats: null, // JSON
      subtaskSummary: null,  // JSON
      loading: true,
      studentId: '',
      showModalSelectStudent: false,
      showModalStartEvaluation: false,
      specifiedData: null,
      latestDeadlineId: null
    };
  },
  computed: {
    ...mapGetters(['isTeacher', 'userId']),

    evaluationsByHomework() {
      const grouped = {};

      this.evaluations.forEach(evaluation => {
        const homework = evaluation.deadline.homework;
        if (!grouped[homework.id]) {
          grouped[homework.id] = {
            homework,
            evaluations: []
          };
        }
        grouped[homework.id].evaluations.push(evaluation);
      });

      // Sort evaluations for each homework from newest to oldest by deadline date.
      Object.values(grouped).forEach(group => {
        group.evaluations.sort((a, b) => new Date(b.deadline.date) - new Date(a.deadline.date));
      });

      return grouped;
    },
    groupedSubtasks() {
      const grouped = {};
      if (!this.subtaskSummary) return grouped;
      this.subtaskSummary.forEach(item => {
        if (!grouped[item.subtask_id]) {
          grouped[item.subtask_id] = {
            subtask_title: item.subtask_title,
            success: 0,
            fail: 0,
            missing: 0,
            container_error: 0,
            to_evaluate: item.to_evaluate
          };
        }
        // Set the outcome counts
        grouped[item.subtask_id][item.outcome] = item.count;
      });

      return grouped;
    }
  },
  async created() {
    try {
      // fetch evaluations for logged in student
      if (!this.isTeacher) {
        const studentId = this.userId;
        this.evaluations = await EvaluationController.fetchEvaluationsWithDeadlineAndHomework(studentId);
      }
      // fetch latest deadline evaluation overview for teacher
      else {
        const deadline = await DeadlineController.fetchLatestDeadline();
        if (deadline) {
          this.latestDeadlineId = deadline.id;
          this.evaluationStats = await EvaluationController.fetchEvaluationStats(deadline.id);
          this.subtaskSummary = await EvaluationController.fetchSubtaskSummary(deadline.id);
        }
      }
    } catch (error) {
      this.$refs.stemErrorModal.show('Error', error.message);
    } finally {
      this.loading = false;
    }
  },
  methods: {
    async fetchAgainEvaluations(studentId = null) {
      this.loading = true;
      try {
        // fetch evaluations for specific student, allow only by teacher
        if (this.isTeacher && studentId) {
          this.evaluations = await EvaluationController.fetchEvaluationsWithDeadlineAndHomework(studentId);
        }
        // fetch for current user (evaluations are only for students)
        else if (!this.isTeacher) {
          studentId = this.userId;
          this.evaluations = await EvaluationController.fetchEvaluationsWithDeadlineAndHomework(studentId);
        }
        // For teachers without studentId - reload the overview page (similar to when load page)
        else if (this.isTeacher && !studentId) {
          this.evaluationStats = null;
          this.subtaskSummary = null;
          this.evaluations = [];
          
          const deadline = await DeadlineController.fetchLatestDeadline();
          if (deadline) {
            this.latestDeadlineId = deadline.id;
            this.evaluationStats = await EvaluationController.fetchEvaluationStats(deadline.id);
            this.subtaskSummary = await EvaluationController.fetchSubtaskSummary(deadline.id);
          } else {
            this.latestDeadlineId = null;
          }
        }
      } catch (error) {
        this.$refs.stemErrorModal.show('Error', error.message);
      } finally {
        this.loading = false;
      }
    },
    formatDate(date) {
      let result = '';
      if (date){
        result = Deadline.formatDateTime(date);
      }
      return result;
    },
    getEvaluationTypeLabel(type) {
      return Deadline.getEvaluationTypeLabel(type);
    },
    async searchStudent(studentUco) {
      try {
        this.loading = true;
        this.evaluationStats = null;
        this.subtaskSummary = null;
        const student = await StudentController.fetchStudentByUCO(studentUco);
        await this.fetchAgainEvaluations(student.id);
      } catch (error) {
        this.$refs.stemErrorModal.show('Error', error.message);
      } finally {
        this.loading = false;
      }
    },
    async fetchEvaluationStatsAndSubtaskSummary(deadlineId) {
      try {
        this.loading = true;
        this.latestDeadlineId = deadlineId;
        this.evaluationStats = await EvaluationController.fetchEvaluationStats(deadlineId);
        this.subtaskSummary = await EvaluationController.fetchSubtaskSummary(deadlineId);
      } catch (error) {
        this.$refs.stemErrorModal.show('Error', error.message);
      } finally {
        this.loading = false;
      }
    },
    async deleteEvaluationRun(deadlineId, deleteDeadline = false) {
      try {
        // Compormation before deletion
        var message = 'Do you want to delete this evaluation run? This will permanently remove all evaluations and their subevaluations.';
        if (deleteDeadline) {
          message = 'Do you want to delete this evaluation run and deadline? This will permanently remove all evaluations, their subevaluations and deadline.';
        }

        const confirmation = confirm(message);
        if (!confirmation) return;

        this.loading = true;
        await EvaluationController.deleteEvaluationRun(deadlineId, deleteDeadline);
        this.$store.dispatch('triggerAlert', {
          title: 'Success',
          message: 'Evaluation run deleted successfully.',
          type: 'success',
        });
        this.evaluationStats = null;
        this.subtaskSummary = null;
        this.evaluations = [];

        // Fetch the latest deadline and update the evaluation stats and subtask summary
        const deadline = await DeadlineController.fetchLatestDeadline();
        if (deadline) {
          this.latestDeadlineId = deadline.id;
          this.fetchEvaluationStatsAndSubtaskSummary(this.latestDeadlineId);
        } else {
          // No deadlines left, clear the latest deadline ID
          this.latestDeadlineId = null;
        }
      } catch (error) {
        this.$refs.stemErrorModal.show('Error', error.message);
      } finally {
        this.loading = false;
      }
    },
    openSpecificStudentList(resultType, subtaskId) {
      this.specifiedData = {
        subevaluation_result_type: resultType,
        deadline_id: this.latestDeadlineId,
        subtask_id: subtaskId
      };

      const modalEl = document.getElementById('specificStudentListModal');
      const modal = new Modal(modalEl);
      modal.show();
    },
    handleRedirectEvaluation(evaluationId) {
      this.$router.push(`evaluation/${evaluationId}`);
    }
  }
}
</script>